import { useState, useEffect } from 'react';
import { Unlock, X, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

export interface PasswordConfirmModalProps {
  open: boolean;
  title?: string;
  description?: string;
  requiredPassword?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export function PasswordConfirmModal({
  open,
  title = 'Autorização de Segurança',
  description = 'Para autorizar esta operação crítica, digite a palavra-chave de segurança:',
  requiredPassword = 'marcelo',
  confirmLabel = 'Liberar Acesso',
  cancelLabel = 'Cancelar',
  onCancel,
  onConfirm,
}: PasswordConfirmModalProps) {
  const [inputVal, setInputVal] = useState('');

  useEffect(() => {
    if (open) {
      setInputVal('');
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputVal.trim().toLowerCase() === requiredPassword.toLowerCase()) {
      onConfirm();
    } else {
      toast.error('Código de autorização inválido.');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 11000,
        background: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 400,
          background: '#ffffff',
          borderRadius: 12,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
          overflow: 'hidden',
          animation: 'ac-modal-pop 0.15s ease-out',
        }}
      >
        {/* Header com gradiente institucional */}
        <div
          style={{
            background: 'linear-gradient(135deg, #1e3a5f, #3730a3)',
            padding: '14px 18px',
            color: '#ffffff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Unlock size={18} />
            <span style={{ fontWeight: 700, fontSize: '.9rem' }}>{title}</span>
          </div>
          <button
            type="button"
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              color: '#ffffff',
              cursor: 'pointer',
              opacity: 0.8,
              padding: 2,
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Corpo do formulário */}
        <form onSubmit={handleSubmit} style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <ShieldAlert size={20} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 2 }} />
            <p style={{ fontSize: '.8rem', color: '#475569', margin: 0, lineHeight: 1.45 }}>
              {description}
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: '.72rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase' }}>
              Código de Autorização
            </label>
            <input
              type="password"
              autoFocus
              className="form-input"
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              placeholder="Digite a palavra-chave..."
              style={{
                background: '#f8fafc',
                border: '1px solid #cbd5e1',
                color: '#1e293b',
                padding: '9px 12px',
                fontSize: '.85rem',
                borderRadius: 8,
              }}
            />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button
              type="button"
              className="btn-cancel"
              onClick={onCancel}
              style={{ fontSize: '.8rem', padding: '8px 14px' }}
            >
              {cancelLabel}
            </button>
            <button
              type="submit"
              className="btn-save"
              style={{
                fontSize: '.8rem',
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #4f46e5, #4338ca)',
                color: '#ffffff',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
